import { useEffect, useMemo, useState } from "react";
import {
  Check,
  Clock,
  Download,
  Trash2,
  AlertTriangle,
  TrendingUp,
  Target,
  Activity,
  Zap,
  ClipboardList,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import type { WpPostRow } from "@/hooks/useExistingPosts";
import type { FinalAction, FinalActionPriority } from "@/services/decisionEngine";
import { useAuditReport } from "@/hooks/useAuditMap";

interface Props {
  posts: WpPostRow[];
  websiteId: string;
  wpPostsSyncedAt: string | null;
  onExportJson: () => void;
  isExporting?: boolean;
  mapId?: string;
  onOpenFullAudit?: () => void;
}

const FINAL_ACTIONS: FinalAction[] = [
  "keep",
  "refurbish",
  "refurbish_url_preserve",
  "keep_outer",
  "delete",
  "consolidate_301",
  "monitor",
];

const ACTION_META: Record<FinalAction, { label: string; color: string; priority: FinalActionPriority }> = {
  consolidate_301: { label: "Kanibalizacja → 301", color: "fuchsia", priority: "high" },
  refurbish_url_preserve: { label: "Przepisz (URL zostaje)", color: "violet", priority: "high" },
  refurbish: { label: "Przepisz", color: "amber", priority: "medium" },
  delete: { label: "Usuń", color: "red", priority: "medium" },
  keep: { label: "Zachowaj", color: "emerald", priority: "low" },
  keep_outer: { label: "Outer cluster", color: "sky", priority: "low" },
  monitor: { label: "Monitor (ręczna)", color: "zinc", priority: "low" },
};

function colorCls(color: string): string {
  const m: Record<string, string> = {
    emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    violet: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30",
    sky: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    red: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
    fuchsia: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-400 border-fuchsia-500/30",
    zinc: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-400 border-zinc-500/30",
  };
  return m[color] || m.zinc;
}

function formatRel(ts: string | null): string {
  if (!ts) return "—";
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true, locale: pl });
  } catch {
    return "—";
  }
}

function maxTimestamp(posts: WpPostRow[], key: keyof WpPostRow): string | null {
  let max: string | null = null;
  for (const p of posts) {
    const v = p[key];
    if (typeof v === "string" && (!max || v > max)) max = v;
  }
  return max;
}

// ============================================================================
// Pipeline step row
// ============================================================================

function PipelineStep({
  label,
  count,
  total,
  done,
}: {
  label: string;
  count: number;
  total: number;
  done: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${
        done
          ? "border-emerald-500/40 bg-emerald-500/10"
          : "border-zinc-500/30 bg-zinc-500/5"
      }`}
    >
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center ${
          done ? "bg-emerald-500 text-white" : "bg-zinc-400 text-white"
        }`}
      >
        {done ? <Check className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{label}</div>
        <div className="text-[10px] text-muted-foreground font-mono">
          {count}/{total}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Mini bar chart
// ============================================================================

function DistBar({
  segments,
  total,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  total: number;
}) {
  if (total === 0) return <div className="h-2 rounded bg-zinc-500/10" />;
  return (
    <div className="flex h-2 rounded overflow-hidden bg-zinc-500/10" title={segments.map((s) => `${s.label}: ${s.value}`).join(" · ")}>
      {segments.map((s, i) => {
        if (s.value === 0) return null;
        const pct = (s.value / total) * 100;
        const colorMap: Record<string, string> = {
          emerald: "bg-emerald-500",
          amber: "bg-amber-500",
          red: "bg-red-500",
          violet: "bg-violet-500",
          sky: "bg-sky-500",
          fuchsia: "bg-fuchsia-500",
          zinc: "bg-zinc-400",
          yellow: "bg-yellow-500",
        };
        return (
          <div
            key={i}
            className={colorMap[s.color] || "bg-zinc-400"}
            style={{ width: `${pct}%` }}
            title={`${s.label}: ${s.value} (${pct.toFixed(0)}%)`}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function PanelSummary({ posts, websiteId, wpPostsSyncedAt, onExportJson, isExporting, mapId, onOpenFullAudit }: Props) {
  const auditQuery = useAuditReport(mapId ?? null);
  const auditL5 = (auditQuery.data?.audit_report as { layers?: { L5_coverage?: Record<string, unknown> } } | null)?.layers?.L5_coverage;
  const [actionLogs, setActionLogs] = useState<{ success: number; failed: number; total: number }>({
    success: 0,
    failed: 0,
    total: 0,
  });
  const [trashCount, setTrashCount] = useState<number>(0);

  useEffect(() => {
    (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: logs } = await (supabase as any)
          .from("wp_action_logs")
          .select("result")
          .eq("website_id", websiteId);
        const rows = (logs || []) as Array<{ result: string }>;
        setActionLogs({
          total: rows.length,
          success: rows.filter((r) => r.result === "success").length,
          failed: rows.filter((r) => r.result === "failed").length,
        });
      } catch {
        /* tabela moze nie istniec — ignoruj */
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { count } = await (supabase as any)
          .from("wp_posts_trash")
          .select("id", { count: "exact", head: true })
          .eq("website_id", websiteId)
          .is("restored_at", null);
        setTrashCount(count ?? 0);
      } catch {
        /* noop */
      }
    })();
  }, [websiteId]);

  const stats = useMemo(() => {
    const total = posts.length;
    const withSro = posts.filter((p) => p.sro_extracted_at).length;
    const withSeo = posts.filter((p) => p.seo_last_sync).length;
    const audited = posts.filter((p) => p.quality_audited_at).length;
    const classified = posts.filter((p) => p.cluster_fit_classified_at).length;
    const withFinalAction = posts.filter((p) => p.final_action).length;

    const quality = {
      keep: posts.filter((p) => p.quality_recommendation === "keep").length,
      borderline: posts.filter((p) => p.quality_recommendation === "borderline").length,
      refurbish: posts.filter((p) => p.quality_recommendation === "refurbish").length,
    };

    const scope = {
      in_scope: posts.filter((p) => p.cluster_fit === "in_scope").length,
      adjacent: posts.filter((p) => p.cluster_fit === "adjacent").length,
      off_topic: posts.filter((p) => p.cluster_fit === "off_topic").length,
      not_classified: posts.filter((p) => !p.cluster_fit).length,
      auto_matched: posts.filter((p) => p.cluster_fit_method === "auto_matched").length,
      llm: posts.filter((p) => p.cluster_fit_method === "llm").length,
      manual: posts.filter((p) => p.cluster_fit_method === "manual_override").length,
    };

    const seo = {
      with_traffic: posts.filter((p) => (p.seo_organic_traffic ?? 0) > 0).length,
      top_3: posts.filter((p) => (p.seo_top_3_count ?? 0) >= 1).length,
      top_10: posts.filter((p) => (p.seo_top_10_count ?? 0) >= 1).length,
      decay: posts.filter((p) => p.seo_content_decay).length,
      cannib: posts.filter((p) => p.seo_has_cannibalization).length,
      total_traffic: posts.reduce((sum, p) => sum + (p.seo_organic_traffic ?? 0), 0),
    };

    const actionCounts = FINAL_ACTIONS.reduce(
      (acc, a) => {
        acc[a] = {
          total: 0,
          locked: 0,
          pending: 0,
          override: 0,
        };
        return acc;
      },
      {} as Record<FinalAction, { total: number; locked: number; pending: number; override: number }>,
    );
    for (const p of posts) {
      if (!p.final_action || !actionCounts[p.final_action]) continue;
      const d = actionCounts[p.final_action];
      d.total++;
      if (p.final_action_locked) d.locked++;
      else d.pending++;
      if (p.final_action_user_override) d.override++;
    }

    const priorityTotals = { high: 0, medium: 0, low: 0 };
    for (const p of posts) {
      if (p.final_action_priority === "high") priorityTotals.high++;
      else if (p.final_action_priority === "medium") priorityTotals.medium++;
      else if (p.final_action_priority === "low") priorityTotals.low++;
    }

    // Matching
    const matchStatusBreakdown = {
      matched_keep: posts.filter((p) => p.quality_recommendation === "keep" && p.final_action).length,
      unmatched: posts.filter((p) => !p.final_action).length,
    };

    // Matrix cluster_fit × quality
    const matrix = {
      in_scope: { keep: 0, borderline: 0, refurbish: 0 },
      adjacent: { keep: 0, borderline: 0, refurbish: 0 },
      off_topic: { keep: 0, borderline: 0, refurbish: 0 },
    };
    for (const p of posts) {
      if (p.cluster_fit && p.quality_recommendation && matrix[p.cluster_fit]) {
        matrix[p.cluster_fit][p.quality_recommendation as "keep" | "borderline" | "refurbish"]++;
      }
    }

    return {
      total,
      withSro,
      withSeo,
      audited,
      classified,
      withFinalAction,
      quality,
      scope,
      seo,
      actionCounts,
      priorityTotals,
      matchStatusBreakdown,
      matrix,
    };
  }, [posts]);

  const timestamps = useMemo(
    () => ({
      sync: wpPostsSyncedAt,
      sro: maxTimestamp(posts, "sro_extracted_at"),
      seo: maxTimestamp(posts, "seo_last_sync"),
      audit: maxTimestamp(posts, "quality_audited_at"),
      cluster: maxTimestamp(posts, "cluster_fit_classified_at"),
      decision: maxTimestamp(posts, "final_action_computed_at"),
    }),
    [posts, wpPostsSyncedAt],
  );

  const total = stats.total;
  const noMatchPct = total > 0 ? (stats.matchStatusBreakdown.unmatched / total) * 100 : 0;

  // ============================================================================
  // Render
  // ============================================================================

  if (total === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          Brak wpisów. Wróć do kroku <strong>1 Pobierz</strong> aby pobrać wpisy z domeny.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* a) Pipeline Progress */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Pipeline Progress (6 etapów)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <PipelineStep label="Sync WP" count={total} total={total} done={!!wpPostsSyncedAt && total > 0} />
            <PipelineStep label="SRO" count={stats.withSro} total={total} done={stats.withSro === total && total > 0} />
            <PipelineStep label="SEO Senuto" count={stats.withSeo} total={total} done={stats.withSeo > 0} />
            <PipelineStep label="Audyt jakości" count={stats.audited} total={stats.withSro} done={stats.audited === stats.withSro && stats.withSro > 0} />
            <PipelineStep label="Cluster Fit" count={stats.classified} total={stats.withSro} done={stats.classified === stats.withSro && stats.withSro > 0} />
            <PipelineStep label="Decision Engine" count={stats.withFinalAction} total={stats.classified} done={stats.withFinalAction === stats.classified && stats.classified > 0} />
          </div>
        </CardContent>
      </Card>

      {/* b) 4 karty głównych wymiarów */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Jakość */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Target className="h-3 w-3" />
              Jakość (A)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <DistBar
              total={stats.audited}
              segments={[
                { label: "keep", value: stats.quality.keep, color: "emerald" },
                { label: "borderline", value: stats.quality.borderline, color: "yellow" },
                { label: "refurbish", value: stats.quality.refurbish, color: "red" },
              ]}
            />
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-emerald-600">keep: {stats.quality.keep}</span>
              <span className="text-yellow-600">borderline: {stats.quality.borderline}</span>
              <span className="text-red-600">refurbish: {stats.quality.refurbish}</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Audytowanych: <strong>{stats.audited}</strong> z {total}
            </p>
          </CardContent>
        </Card>

        {/* Scope */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Target className="h-3 w-3" />
              Scope (C)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <DistBar
              total={stats.classified}
              segments={[
                { label: "in_scope", value: stats.scope.in_scope, color: "emerald" },
                { label: "adjacent", value: stats.scope.adjacent, color: "amber" },
                { label: "off_topic", value: stats.scope.off_topic, color: "red" },
              ]}
            />
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-emerald-600">in: {stats.scope.in_scope}</span>
              <span className="text-amber-600">adj: {stats.scope.adjacent}</span>
              <span className="text-red-600">off: {stats.scope.off_topic}</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              auto_matched: <strong>{stats.scope.auto_matched}</strong> · llm: <strong>{stats.scope.llm}</strong>
              {stats.scope.manual > 0 && <> · manual: <strong>{stats.scope.manual}</strong></>}
            </p>
          </CardContent>
        </Card>

        {/* SEO */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3" />
              SEO (B+D)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-[11px]">
            <div className="flex justify-between">
              <span className="text-muted-foreground">z ruchem</span>
              <strong>{stats.seo.with_traffic}</strong>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">top 3</span>
              <strong>{stats.seo.top_3}</strong>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">top 10</span>
              <strong>{stats.seo.top_10}</strong>
            </div>
            <div className="flex justify-between text-amber-600">
              <span>decay</span>
              <strong>{stats.seo.decay}</strong>
            </div>
            <div className="flex justify-between text-fuchsia-600">
              <span>kanib</span>
              <strong>{stats.seo.cannib}</strong>
            </div>
            <div className="pt-1 border-t border-border/40 text-[10px] text-muted-foreground">
              Suma traffic: <strong className="text-foreground">~{Math.round(stats.seo.total_traffic)}</strong>/mies
            </div>
          </CardContent>
        </Card>

        {/* Decyzje */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Zap className="h-3 w-3" />
              Decyzje (E)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <DistBar
              total={stats.withFinalAction}
              segments={[
                { label: "HIGH", value: stats.priorityTotals.high, color: "red" },
                { label: "MEDIUM", value: stats.priorityTotals.medium, color: "amber" },
                { label: "LOW", value: stats.priorityTotals.low, color: "zinc" },
              ]}
            />
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-red-600">🚨 {stats.priorityTotals.high}</span>
              <span className="text-amber-600">⚡ {stats.priorityTotals.medium}</span>
              <span className="text-zinc-600">💤 {stats.priorityTotals.low}</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Obliczonych: <strong>{stats.withFinalAction}</strong> z {total}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* c) Matrix cluster_fit × quality */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Matrix — Scope × Jakość</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="text-left py-2 pr-3"></th>
                  <th className="text-center py-2 px-3 font-semibold text-emerald-600">keep</th>
                  <th className="text-center py-2 px-3 font-semibold text-yellow-600">borderline</th>
                  <th className="text-center py-2 px-3 font-semibold text-red-600">refurbish</th>
                  <th className="text-center py-2 px-3 font-semibold text-muted-foreground">Σ</th>
                </tr>
              </thead>
              <tbody>
                {(["in_scope", "adjacent", "off_topic"] as const).map((cf) => {
                  const row = stats.matrix[cf];
                  const sum = row.keep + row.borderline + row.refurbish;
                  return (
                    <tr key={cf} className="border-t border-border/40">
                      <td className="py-2 pr-3 font-semibold text-xs">
                        <span className={`inline-block px-2 py-0.5 rounded ${cf === "in_scope" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : cf === "adjacent" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" : "bg-red-500/15 text-red-700 dark:text-red-400"}`}>
                          {cf}
                        </span>
                      </td>
                      <td className="text-center py-2 px-3 tabular-nums">{row.keep}</td>
                      <td className="text-center py-2 px-3 tabular-nums">{row.borderline}</td>
                      <td className="text-center py-2 px-3 tabular-nums">{row.refurbish}</td>
                      <td className="text-center py-2 px-3 tabular-nums font-semibold text-muted-foreground">{sum}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* d) Matching stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Matching (node ↔ wpis)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="rounded-md border p-2 bg-background/60">
              <p className="text-[10px] uppercase text-muted-foreground">Wszystkie</p>
              <p className="text-2xl font-bold">{total}</p>
            </div>
            <div className="rounded-md border border-emerald-500/30 p-2 bg-emerald-500/5">
              <p className="text-[10px] uppercase text-emerald-600">Zmatchowane (scope=in)</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.scope.in_scope + stats.scope.adjacent}</p>
            </div>
            <div className="rounded-md border border-amber-500/30 p-2 bg-amber-500/5">
              <p className="text-[10px] uppercase text-amber-600">Niezmatchowane / unclassified</p>
              <p className="text-2xl font-bold text-amber-600">{stats.scope.not_classified}</p>
            </div>
          </div>
          {noMatchPct > 30 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs">
              <AlertTriangle className="h-4 w-4 inline mr-1 text-amber-600" />
              Ponad <strong>{noMatchPct.toFixed(0)}%</strong> wpisów nie ma obliczonej akcji.
              Rozważ uruchomienie kroków 2-4 dla brakujących wpisów.
            </div>
          )}
        </CardContent>
      </Card>

      {/* e) Akcje stats — detailed breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Akcje — detal per rekomendacja
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            {FINAL_ACTIONS.filter((a) => stats.actionCounts[a].total > 0)
              .sort((a, b) => {
                const prioOrder = { high: 0, medium: 1, low: 2 };
                const pa = prioOrder[ACTION_META[a].priority];
                const pb = prioOrder[ACTION_META[b].priority];
                if (pa !== pb) return pa - pb;
                return stats.actionCounts[b].total - stats.actionCounts[a].total;
              })
              .map((a) => {
                const d = stats.actionCounts[a];
                const meta = ACTION_META[a];
                return (
                  <div
                    key={a}
                    className={`flex items-center gap-3 rounded-md border px-3 py-2 text-xs ${colorCls(meta.color)}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{meta.label}</span>
                        <Badge variant="outline" className="text-[9px] uppercase">
                          {meta.priority}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] tabular-nums">
                      <span title="Razem">Σ <strong>{d.total}</strong></span>
                      <span className="text-emerald-600" title="Zaakceptowane (locked)">
                        ✓ {d.locked}
                      </span>
                      <span className="text-amber-600" title="Oczekują akceptacji">
                        ⏳ {d.pending}
                      </span>
                      {d.override > 0 && (
                        <span className="text-violet-600" title="Manual override">
                          ✎ {d.override}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            {stats.withFinalAction === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Brak decyzji. Wróć do kroku <strong>4 Decyzje</strong> i kliknij "Przelicz akcje".
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* f) Timestamps */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Ostatnie uruchomienia
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Sync WP</p>
              <p className="font-semibold">{formatRel(timestamps.sync)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">SRO extraction</p>
              <p className="font-semibold">{formatRel(timestamps.sro)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">SEO Senuto</p>
              <p className="font-semibold">{formatRel(timestamps.seo)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Audyt jakości</p>
              <p className="font-semibold">{formatRel(timestamps.audit)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Cluster Fit</p>
              <p className="font-semibold">{formatRel(timestamps.cluster)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Decision Engine</p>
              <p className="font-semibold">{formatRel(timestamps.decision)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* g+h) Action logs + Trash */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Logi akcji (wp_action_logs)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Razem zarejestrowanych</span>
              <strong>{actionLogs.total}</strong>
            </div>
            <div className="flex justify-between text-emerald-600">
              <span>Sukces</span>
              <strong>{actionLogs.success}</strong>
            </div>
            {actionLogs.failed > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Błędy</span>
                <strong>{actionLogs.failed}</strong>
              </div>
            )}
            {actionLogs.total === 0 && (
              <p className="text-muted-foreground">
                Brak logów — akcje jeszcze nie były wykonane. Wróć do kroku <strong>5 Wykonaj</strong>.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              Kosz (wp_posts_trash)
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Miękko usuniętych wpisów</span>
              <strong className="text-red-600">{trashCount}</strong>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Wpisy mają status <code>draft</code> w WP. Hard delete za 30 dni (faza backlog).
              Przywracanie z kosza — UI w planach.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* j) Map Coverage preview (M16 L5 cross-ref) */}
      {mapId && auditL5 && (
        <Card className="border-violet-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Pokrycie mapy wpisami WP (Audyt L5)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs">
            <div className="flex items-center gap-3">
              <span className="text-muted-foreground">Global matched ratio</span>
              <span className="text-lg font-bold tabular-nums">
                {Math.round(((auditL5 as { matched_ratio_global?: number }).matched_ratio_global ?? 0) * 100)}%
              </span>
            </div>
            {(() => {
              const ghosts = (auditL5 as { ghost_seeds?: Array<{ seed_name: string; priority: number; severity: string }> }).ghost_seeds;
              const rev = (auditL5 as { reverse_node_suggestions?: unknown[] }).reverse_node_suggestions;
              return (
                <div className="flex items-center gap-4">
                  <span className="text-muted-foreground">
                    Ghost seeds:{" "}
                    <strong className={(ghosts?.length ?? 0) > 0 ? "text-red-600" : ""}>
                      {ghosts?.length ?? 0}
                    </strong>
                  </span>
                  <span className="text-muted-foreground">
                    Reverse node suggestions:{" "}
                    <strong>{(rev as unknown[])?.length ?? 0}</strong>
                  </span>
                </div>
              );
            })()}
            {onOpenFullAudit && (
              <Button variant="outline" size="sm" onClick={onOpenFullAudit}>
                Zobacz pełny audyt mapy →
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* i) Export */}
      <Card className="border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-transparent">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4" />
            Eksport danych (JSON)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Wygeneruje plik <code>~500KB-1MB</code> z wszystkimi danymi zebranymi w tej
            podstronie (meta mapy, top entities, 88 wpisów z SRO + quality + SEO + cluster_fit +
            final_action + matching + rozkłady). Do uploadu w czacie LLM dla zewnętrznej analizy.
          </p>
          <Button onClick={onExportJson} disabled={isExporting} className="bg-violet-600 hover:bg-violet-700 text-white">
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? "Generowanie..." : "📦 Eksportuj dane (JSON)"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
